/**
 * Admin auth context — exposes the current principal (or null) to the tree.
 *
 * Auth is the realm session. main.tsx installs its live token as the
 * api-client's token provider; useCurrentAdmin() consumes /me; this context
 * surfaces the result, and any refusal by the API, to the rest of the tree.
 */
import { createContext, useContext, type ReactNode } from 'react';

import type { Me } from '@optum/api-client/types';

import { isInternal, isPlatformAdmin, useCurrentAdmin } from './use-current-admin.js';

export interface AuthContextValue {
  user: Me | null;
  /** The API's reason when it refused a token the app sent; null otherwise. */
  refusal: string | null;
  /** `/me` failed for another reason (the API down, the network gone). */
  isError: boolean;
  isLoading: boolean;
  isFetching: boolean;
  /** Asks `/me` again. */
  retry: () => void;
  isInternal: boolean;
  isPlatformAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  refusal: null,
  isError: false,
  isLoading: true,
  isFetching: true,
  retry: () => {},
  isInternal: false,
  isPlatformAdmin: false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user, refusal, isError, isLoading, isFetching, retry } = useCurrentAdmin();
  const value: AuthContextValue = {
    user,
    refusal,
    isError,
    retry,
    isLoading,
    isFetching,
    isInternal: isInternal(user),
    isPlatformAdmin: isPlatformAdmin(user),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
